/**
 * TD Bazaarvoice Heart Rating
 * Web component that displays Bazaarvoice ratings as heart icons
 * Observes the BV inline rating container and extracts rating data
 */

if (!customElements.get('td-bv-heart-rating')) {
  const SELECTORS = {
    bvSource: '[data-td-bv-source]',
    hearts: '[data-td-bv-hearts]',
    number: '[data-td-bv-rating-number]',
    count: '[data-td-bv-rating-count]',
    countValue: '[data-td-bv-rating-count-value]',
    foreground: '.td-bv-hearts__foreground'
  };
  const AGGREGATE_RATING_BEST_RATING = 5;
  const AGGREGATE_RATING_WORST_RATING = 1;

  class TdBvHeartRating extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
      this.bvContainer = null;
      this.heartsContainer = null;
      this.numberContainer = null;
      this.countContainer = null;
      this.countValueContainer = null;
    }

    connectedCallback() {
      this.bvContainer = this.querySelector(SELECTORS.bvSource);
      this.heartsContainer = this.querySelector(SELECTORS.hearts);
      this.numberContainer = this.querySelector(SELECTORS.number);
      this.countContainer = this.querySelector(SELECTORS.count);
      this.countValueContainer = this.querySelector(SELECTORS.countValue);
      
      if (!this.bvContainer) return;
      
      this.setupObserver();
      this.extractAndDisplayRating();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    /**
     * Sets up MutationObserver to watch for BV content injection
     */
    setupObserver() {
      this.observer = new MutationObserver(() => {
        this.extractAndDisplayRating();
      });

      this.observer.observe(this.bvContainer, {
        childList: true,
        subtree: true,
        characterData: true
      });

    }

    /**
     * Extracts rating data from BV widget and updates heart display
     */
    extractAndDisplayRating() {
      const ratingData = this.extractRatingFromBV();
      
      if (ratingData.rating !== null) {
        this.updateHearts(ratingData.rating);
        this.updateNumber(ratingData.rating);
        this.updateCount(ratingData.count);
        this.upsertAggregateRatingJson(ratingData);
        this.classList.add('td-bv-heart-rating--loaded');
      }
    }

    /**
     * Attempts to extract rating value from BV injected content
     * @returns {Object} { rating: number|null, count: number|null }
     */
    extractRatingFromBV() {
      const ratingSelectors = [
        '[itemprop="ratingValue"]',
        '.bv-rating-ratio',
        '.bv-rating',
        '.bv_avgRating_component_container [aria-label]',
        '.bv-stars-container [aria-label]',
        '[aria-label*="out of 5"]',
        '[aria-label*="rating"]',
        '[aria-label*="Rating"]'
      ];
      const countSelectors = [
        '[itemprop="reviewCount"]',
        '.bv_numReviews_text',
        '.bv-review-count',
        '[aria-label*="reviews"]',
        '[aria-label*="Reviews"]'
      ];
      const normalizedText = this.normalizeText(this.bvContainer.textContent);

      const rating = this.extractFirstMatch(ratingSelectors, (value) => this.parseRating(value))
        ?? this.parseRating(normalizedText);
      const count = this.extractFirstMatch(countSelectors, (value) => this.parseCount(value))
        ?? this.parseCount(normalizedText);

      return { rating, count };
    }

    /**
     * Updates the heart foreground width based on rating
     * @param {number} rating - Rating value (0-5)
     */
    updateHearts(rating) {
      if (!this.heartsContainer) return;
      
      // Class selector is allowed here because the SVG foreground node is static, internal markup.
      const foreground = this.heartsContainer.querySelector(SELECTORS.foreground);
      if (foreground) {
        const percentage = (rating / 5) * 100;
        foreground.style.width = `${percentage}%`;
      }
    }

    upsertAggregateRatingJson({ rating, count }) {
      if (rating === null || count === null || count <= 0) return;

      const productId = this.dataset.tdProductId;
      const productName = this.dataset.tdProductName;
      const productUrl = this.dataset.tdProductUrl;

      if (!productId || !productName || !productUrl) return;

      const scriptId = `td-product-aggregate-rating-${productId}`;
      const jsonScript = document.getElementById(scriptId) || document.createElement('script');
      const payload = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        '@id': productUrl,
        name: productName,
        url: productUrl,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: rating.toFixed(1),
          ratingCount: count,
          reviewCount: count,
          bestRating: AGGREGATE_RATING_BEST_RATING,
          worstRating: AGGREGATE_RATING_WORST_RATING
        }
      };

      jsonScript.id = scriptId;
      jsonScript.type = 'application/ld+json';
      jsonScript.textContent = JSON.stringify(payload);

      if (!jsonScript.parentNode) {
        document.head.appendChild(jsonScript);
      }
    }

    updateNumber(rating) {
      if (!this.numberContainer) return;

      this.numberContainer.textContent = `${rating.toFixed(1)}/5`;
    }

    /**
     * Updates the review count display
     * @param {number|null} count - Review count
     */
    updateCount(count) {
      if (!this.countContainer || !this.countValueContainer) return;
      
      if (count !== null) {
        this.countValueContainer.textContent = count;
        this.countContainer.dataset.hasContent = 'true';
      } else {
        this.countContainer.dataset.hasContent = 'false';
      }
    }

    extractFirstMatch(selectors, parser) {
      for (const selector of selectors) {
        const elements = this.bvContainer.querySelectorAll(selector);

        for (const element of elements) {
          const candidate = parser(this.getElementCandidateText(element));
          if (candidate !== null) {
            return candidate;
          }
        }
      }

      return null;
    }

    getElementCandidateText(element) {
      return this.normalizeText(
        [
          element.getAttribute('aria-label'),
          element.getAttribute('content'),
          element.textContent
        ]
          .filter(Boolean)
          .join(' ')
      );
    }

    normalizeText(value) {
      return (value || '').replace(/\s+/g, ' ').trim();
    }

    parseRating(value) {
      if (!value) return null;

      const labeledPatterns = [
        /(\d+(?:\.\d+)?)\s*(?:out of|\/)\s*5/i,
        /rating[:\s]*(\d+(?:\.\d+)?)/i
      ];

      for (const pattern of labeledPatterns) {
        const match = value.match(pattern);
        if (!match) continue;

        const parsedValue = parseFloat(match[1]);
        if (!Number.isNaN(parsedValue) && parsedValue >= 0 && parsedValue <= 5) {
          return parsedValue;
        }
      }

      const numericMatches = value.match(/\d+(?:\.\d+)?/g) || [];
      for (const numericMatch of numericMatches) {
        const parsedValue = parseFloat(numericMatch);
        if (!Number.isNaN(parsedValue) && parsedValue >= 0 && parsedValue <= 5) {
          return parsedValue;
        }
      }

      return null;
    }

    parseCount(value) {
      if (!value) return null;

      const patterns = [
        /based on\s*(\d+)\s*reviews?/i,
        /(\d+)\s*reviews?/i,
        /\((\d+)\)/
      ];

      for (const pattern of patterns) {
        const match = value.match(pattern);
        if (!match) continue;

        const parsedValue = parseInt(match[1], 10);
        if (!Number.isNaN(parsedValue)) {
          return parsedValue;
        }
      }

      return null;
    }
  }

  customElements.define('td-bv-heart-rating', TdBvHeartRating);
}
